import { createFileRoute } from "@tanstack/react-router"
import { useSuppressProjectMap } from "@/contexts/ProjectMapVisibility"
import { SpeciesObservationForm } from "@/components/observations/SpeciesObservationForm"
import * as z from "zod"
import type {
  ExistingSpeciesValue,
  SpeciesValue,
} from "@/components/observations/SelectedSpeciesItem"
import { toAlaKingdomOrNull } from "@fieldbook/common/utils"
import { useObservation } from "@/hooks/useObservation"
import { useSpecies } from "@/hooks/useSpecies"
import { useSpeciesProfile } from "@/hooks/useSpeciesProfile"
import { Spinner } from "@fieldbook/ui/spinner"
import { useMemo } from "react"

const searchSchema = z.object({
  siteId: z.string().optional(),
  speciesId: z.string().optional(),
  duplicateFromId: z.string().optional(),
  draftId: z.string().uuid().optional(),
})

export const Route = createFileRoute(
  "/_private/projects/$id/observations/new/species",
)({
  component: NewSpeciesObservation,
  validateSearch: (search) => searchSchema.parse(search),
})

function NewSpeciesObservation() {
  // This route covers the project map for its whole life, loading states
  // included. Releasing the map's WebGL context and tile buffers here keeps
  // the process small enough to survive being backgrounded behind the camera.
  useSuppressProjectMap()

  const { id: projectId } = Route.useParams()
  const { siteId, speciesId, duplicateFromId, draftId } = Route.useSearch()

  const { data: fieldbookSpecies, isPending: isSpeciesPending } = useSpecies(
    speciesId ?? undefined,
  )
  const { data: profileSpecies, isPending: isProfilePending } =
    useSpeciesProfile(speciesId ?? "")

  const speciesValue = useMemo<SpeciesValue | undefined>(() => {
    if (!speciesId) return undefined
    if (fieldbookSpecies) {
      return {
        speciesId,
        type: "existing",
        name: fieldbookSpecies.name,
        alaGuid: fieldbookSpecies.ala_guid,
        kingdom: toAlaKingdomOrNull(fieldbookSpecies.kingdom),
      } satisfies ExistingSpeciesValue
    }
    if (profileSpecies) {
      return {
        speciesId,
        type: "profile",
        profileSource: profileSpecies.source,
        alaGuid: profileSpecies.ala_guid,
        name: profileSpecies.name,
      } as SpeciesValue
    }
    return undefined
  }, [speciesId, fieldbookSpecies, profileSpecies])

  const { data: duplicateFrom, isPending: isDuplicatePending } = useObservation(
    { observationId: duplicateFromId ?? "", projectId },
  )

  const isLoading =
    (speciesId && isSpeciesPending && isProfilePending) ||
    (duplicateFromId && isDuplicatePending)

  if (isLoading) {
    return <Spinner />
  }

  return (
    <SpeciesObservationForm
      projectId={projectId}
      siteId={siteId}
      speciesId={speciesId}
      speciesValue={speciesValue}
      duplicateFrom={duplicateFromId ? (duplicateFrom ?? undefined) : undefined}
      draftId={draftId}
    />
  )
}
